import {
  ArrowBack
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';

const PatientForm = ({ onSubmit, leadId, mode, onBackToList }) => {

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    advised: [],
    serviceComment: ''
  });
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const advisedOptions = [
    'Advised Glasses',
    'Keratoconus',
    'Normal',
    'Refer To Hospital',
    'Squint',
    'Cataract',
    'Lasik',
    'Pterygium',
    'Retina'
  ];

  // Load lead details when in edit mode
  useEffect(() => {
    const loadLead = async () => {
      if (mode === 'edit' && leadId) {
        setApiError('');
        try {
          const token = localStorage.getItem('accessToken');
          const resp = await fetch(`https://camps.vasaneye.com/vec-camps-backend/leads/${leadId}`, {
            headers: {
              'x-vec-camp-session-token': token
            }
          });
          if (resp.ok) {
            const data = await resp.json();
            console.log('Lead data received:', data); // Debug log
            setFormData({
              firstName: data.first_name || '',
              lastName: data.last_name || '',
              phone: data.mobile || '',
              dateOfBirth: data.date_of_birth || '',
                gender: data.gender || '',
              advised: data.description ? data.description.split(',').map(item => item.trim()) : [],
                serviceComment: data.service_comment || '',
                age: data.age ? String(data.age): ''
            });
          } else {
            setApiError('Failed to load lead details.');
          }
        } catch (e) {
          setApiError('Network error while loading lead.');
        }
      }
    };
    loadLead();
  }, [mode, leadId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Set the form value normally; do NOT auto-populate age when DOB changes
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // const computeAgeFromDob = (dob) => {
  //   if (!dob) return null;
  //   const b = new Date(dob);
  //   if (isNaN(b.getTime())) return null;
  //   const today = new Date();
  //   let age = today.getFullYear() - b.getFullYear();
  //   const m = today.getMonth() - b.getMonth();
  //   if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  //   return age >= 0 ? age : null;
  // };

  const handlePhoneInput = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 12) {
      setFormData(prev => ({
        ...prev,
        phone: value
      }));
    }
  };

  const handleAdvisedChange = (advised) => {
    setFormData(prev => ({
      ...prev,
      advised: prev.advised.includes(advised)
        ? prev.advised.filter(item => item !== advised)
        : [...prev.advised, advised]
    }));
    setErrors(prev => ({ ...prev, advised: '' })) 
  };

  const validateForm = () => {
    const newErrors = {};

    // if (!formData.firstName.trim()) {
    //   newErrors.firstName = 'First Name is required';
    // }

    if (mode !== 'edit' && !formData.lastName.trim()) {
      newErrors.lastName = 'Last Name is required';
    }

    if (mode !== 'edit' && !formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,12}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-12 digit phone number';
    }

    // In create mode require either DOB or age
    if (mode !== 'edit' && !formData.dateOfBirth && !formData.age) {
      newErrors.dateOfBirth = 'Either Date of Birth or Age is required';
    } else if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of Birth cannot be in the future';
      }
    }

    // If age is provided, validate it's numeric and within range
    if (formData.age) {
      const ageNum = parseInt(String(formData.age).replace(/[^0-9]/g, ''), 10);
      if (isNaN(ageNum) || ageNum < 0 || ageNum > 130) {
        newErrors.age = 'Please enter a valid age (0-130)';
      }
    }

    if (mode !== 'edit' && !formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    // Service Comments required (edit mode only)
    // if (mode === 'edit' && !formData.serviceComment.trim()) {
    //   newErrors.serviceComment = 'Service Comments is required';
    // }

    // Advised required (edit mode only)
    if (mode === 'edit' && (!formData.advised || formData.advised.length === 0)) {
      newErrors.advised = 'Advised is required';
      console.log("Advised error set")
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Updating.......")
    if (validateForm()) {
      setApiError('');
      setSubmitting(true);
      try {

        if (mode === 'edit' && leadId) {
          // Only send optional fields if not empty
          const payload = {
            description: formData.advised.join(', '),
          };
          if (formData.serviceComment && formData.serviceComment.trim() !== '') payload.service_comment = formData.serviceComment.trim();
          if (formData.firstName && formData.firstName.trim() !== '') payload.first_name = formData.firstName.trim();
          if (formData.lastName && formData.lastName.trim() !== '') payload.last_name = formData.lastName.trim();
          if (formData.dateOfBirth && formData.dateOfBirth !== '') payload.date_of_birth = formData.dateOfBirth;
          if (formData.gender && formData.gender !== '') payload.gender = formData.gender;
          if (formData.phone && formData.phone.trim() !== '') payload.mobile = formData.phone.trim();
          if (formData.age && String(formData.age).trim() !== '') payload.age = String(String(formData.age).replace(/[^0-9]/g, ''));

          const token = localStorage.getItem('accessToken');
          const response = await fetch(`https://camps.vasaneye.com/vec-camps-backend/leads/${leadId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-vec-camp-session-token': token
            },
            body: JSON.stringify(payload)
          });

          const data = await response.json().catch(() => ({}));
          if (response.ok) {
            if (onBackToList) onBackToList();
          } else {
            setApiError(data.message || 'Failed to update lead. Please try again.');
          }

        } else {
          const payload = {};

          if (formData.lastName && formData.lastName.trim() !== '') payload.last_name = formData.lastName.trim();
          if (formData.dateOfBirth && formData.dateOfBirth !== '') payload.date_of_birth = formData.dateOfBirth;
          if (formData.gender && formData.gender !== '') payload.gender = formData.gender;
          if (formData.phone && formData.phone.trim() !== '') payload.mobile = formData.phone.trim();
          if (formData.firstName && formData.firstName.trim() !== '') payload.first_name = formData.firstName.trim();
          if (formData.serviceComment && formData.serviceComment.trim() !== '') payload.service_comment = formData.serviceComment.trim();
          if (formData.advised && formData.advised.length > 0) payload.description = formData.advised.join(', ');
          if (formData.age && String(formData.age).trim() !== '') payload.age = String(String(formData.age).replace(/[^0-9]/g, ''));

          const campDataRaw = localStorage.getItem('campData');

          if (campDataRaw) {
            try {
              const campData = JSON.parse(campDataRaw);

              if (campData.city) payload.city = campData.city;
              if (campData.state) payload.state = campData.state;

            } catch (error) {
              console.error("Invalid campData JSON in localStorage", error);
            }
          }

          const token = localStorage.getItem('accessToken');
          const response = await fetch('https://camps.vasaneye.com/vec-camps-backend/leads/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'x-vec-camp-session-token': token
            },
            body: JSON.stringify(payload)
          });

          const data = await response.json().catch(() => ({}));

          if (response.ok && (data.success === true || data.message === 'Lead created successfully')) {
            // Optionally inform parent state for local UI
            if (onSubmit) {
              onSubmit({
                patientName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                phone: formData.phone.trim(),
                age: formData.age ? String(String(formData.age).replace(/[^0-9]/g, '')) : undefined,
                gender: formData.gender
              });
            }

            setShowSuccess(true);
            setFormData({
              firstName: '',
              lastName: '',
              phone: '',
              dateOfBirth: '',
              age: '',
              gender: '',
              advised: [],
              serviceComment: ''
            });
          } else {
            setApiError(data.message || 'Failed to create lead. Please try again.');
          }
        }
      } catch (err) {
        setApiError('Network error. Please check your connection and try again.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  if (showSuccess) {
    return (
      <Dialog open={showSuccess} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" sx={{ color: '#16a34a', fontWeight: 'bold' }}>
            Success!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Thank you! Your response has been recorded.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setShowSuccess(false)}
            sx={{ backgroundColor: '#1e3a8a' }}
          >
            Create New Lead
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0'
          }}
        >
          {/* Back Button for Edit Mode */}
          {mode === 'edit' && onBackToList && (
            <Box sx={{ mb: 3 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={onBackToList}
                variant="outlined"
                sx={{
                  color: '#1e3a8a',
                  borderColor: '#1e3a8a',
                  '&:hover': {
                    backgroundColor: '#1e3a8a',
                    color: 'white',
                    borderColor: '#1e3a8a',
                  },
                }}
              >
                Back to Leads List
              </Button>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {apiError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {apiError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Row 1: First Name + Last Name */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    First Name *
                  </Typography>
                  <TextField
                    fullWidth
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                    placeholder="Enter first name"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    Last Name *
                  </Typography>
                  <TextField
                    fullWidth
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                    placeholder="Enter last name"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Row 2: Phone + Date of Birth */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    Phone Number *
                  </Typography>
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneInput}
                    error={!!errors.phone}
                    helperText={errors.phone || "We'll contact you on this number"}
                    placeholder="10–12 digit mobile number"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    Date of Birth *
                  </Typography>
                  <TextField
                    fullWidth
                    name="dateOfBirth"
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0]
                    }}
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', md: 140 } }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    Age
                  </Typography>
                  <TextField
                    fullWidth
                    name="age"
                    value={formData.age}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9]/g, '').slice(0,3);
                      setFormData(prev => ({ ...prev, age: v }));
                      if (errors.age) setErrors(prev => ({ ...prev, age: '' }));
                    }}
                    error={!!errors.age}
                    helperText={errors.age}
                    placeholder="Years"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Row 3: Gender + Service Comments (edit mode only) */}
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                <FormControl error={!!errors.gender} sx={{ flex: 1 }}>
                  <FormLabel component="legend" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                    Gender *
                  </FormLabel>
                  <Tabs
                    value={formData.gender}
                    onChange={(e, value) => setFormData(prev => ({ ...prev, gender: value }))}
                    variant="fullWidth"
                    sx={{
                      backgroundColor: '#f5f5f5',
                      border: '1px solid',
                      borderColor: errors.gender ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                      borderRadius: '8px',
                      padding: '4px',
                      transition: 'border-color 0.2s ease',
                      '&:hover': { borderColor: '#1e3a8a' },
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '5px',
                        margin: '0 2px',
                        minHeight: '48px',
                        color: '#374151',
                        backgroundColor: 'transparent',
                        '&.Mui-selected': {
                          backgroundColor: '#1e3a8a',
                          color: 'white',
                          boxShadow: '0 2px 4px rgba(30, 58, 138, 0.3)',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(30, 58, 138, 0.1)',
                          color: '#1e3a8a',
                        },
                      },
                      '& .MuiTabs-indicator': {
                        display: 'none',
                      },
                    }}
                  >
                    <Tab label="Male" value="Male" />
                    <Tab label="Female" value="Female" />
                    <Tab label="Other" value="Other" />
                  </Tabs>
                  {errors.gender && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      {errors.gender}
                    </Typography>
                  )}
                </FormControl>
                {mode === 'edit' && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1, color: '#374151', fontWeight: 500 }}>
                      Service Comments
                    </Typography>
                    <TextField
                      fullWidth
                      name="serviceComment"
                      multiline
                      rows={4}
                      value={formData.serviceComment}
                      onChange={handleInputChange}
                      error={!!errors.serviceComment}
                      helperText={errors.serviceComment}
                      placeholder="Enter service comments"
                      variant="outlined"
                    />
                  </Box>
                )}
                {mode !== 'edit' && <Box sx={{ flex: 1 }} />}
              </Box>

              {mode === 'edit' && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 2, color: '#374151', fontWeight: 500 }}>
                    Advised
                  </Typography>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    rowGap: { xs: 0.5, md: 1 },
                    columnGap: { xs: 0.5, md: 2 },
                    maxHeight: { xs: 'none', md: 240 },
                    overflowY: { xs: 'visible', md: 'auto' }
                  }}>
                    {advisedOptions.map(option => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={formData.advised.includes(option)}
                            onChange={() => handleAdvisedChange(option)}
                            size="small"
                          />
                        }
                        label={option}
                        sx={{ display: 'block', mb: { xs: 0, md: 0.5 }, mr: 0 }}
                      />
                    ))}
                  </Box>
                  {errors.advised &&(
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontSize: '1rem' }}>
                    {errors.advised}
                  </Typography>
                )}
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 6, textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                sx={{
                  backgroundColor: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  borderRadius: '5px',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(30, 58, 138, 0.3)',
                  '&:hover': {
                    backgroundColor: '#1e40af',
                    boxShadow: '0 6px 20px rgba(30, 58, 138, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
                disabled={submitting}
              >
                {submitting ? (mode === 'edit' ? 'Updating…' : 'Submitting…') : (mode === 'edit' ? 'Update' : 'Submit')}
              </Button>
              {mode === 'edit' && (
                <Button sx={{ ml: 2 }} onClick={() => onBackToList && onBackToList()}>Cancel</Button>
              )}
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PatientForm;
